import React, { useReducer, useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import AdminNavbar from "./admin/AdminNav";
import Navbar from "./navigation";
import VerticalStepper from "./verticalStepper";
import CastingCallForm from "./director/components/CastingCallForm";
import {
  formReducer,
  initialFormState,
} from "./director/components/postCastingCallReducer";
import { Snackbar, Alert, Container, Box, Grid } from "@mui/material";
import api from "./api/api";
import { ENDPOINTS } from "./api/endpoints";
import Footer from "./Footer";

function PostCastingCall() {
  const navigate = useNavigate();
  const location = useLocation();

  const { noticeId: urlNoticeId } = useParams();
  const [formData, dispatch] = useReducer(formReducer, initialFormState);

  const [errors, setErrors] = useState({});
  const [fileError, setFileError] = useState("");
  const [filewordError, setFilewordError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [payTypeOptions, setPayTypeOptions] = useState([]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [directorList, setDirectorList] = useState([]);
  const [selectedDirector, setSelectedDirector] = useState(null);
  const [selectedDirectorId, setSelectedDirectorId] = useState("");
  const [directorDropdownError, setDirectorDropdownError] = useState("");

  const [locationOptions, setLocationOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [unionOptions, setUnionOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [currentStep, setCurrentStep] = useState(0);
  const userId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");

  const isAdmin = userRole === "ADMIN";
  const isEditMode = Boolean(urlNoticeId);
  const showDirectorDropdown = isAdmin && !isEditMode;

  const [touched, setTouched] = useState({});
  const [manualRushOverride, setManualRushOverride] = useState(false);

  const [disabledFields, setDisabledFields] = useState({
    contactName: false,
    company: false,
    city: false,
    contactEmail: false,
    contactPhone: false,
  });

  const [directorSearch, setDirectorSearch] = useState("");
  const [directorResults, setDirectorResults] = useState([]);
  const [directorLoading, setDirectorLoading] = useState(false);

  /* ---------------------------------------------
      FETCH INITIAL DATA
  ----------------------------------------------*/
  useEffect(() => {
    fetchLocations();
    fetchCategories();
    fetchUnions();
    fetchPayTypes();

    if (isAdmin) fetchDirectors();
  }, []);

  /* --------------------------------------
      DIRECTOR SERVER SEARCH (debounced)
  ---------------------------------------*/
  useEffect(() => {
    if (!showDirectorDropdown) return;

    if (!directorSearch || directorSearch.length < 2) {
      setDirectorResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setDirectorLoading(true);

        const rolesRes = await api.get("/api/Admin/GetAllRoles");
        const companyRole = rolesRes.data?.data?.find(
          (r) => r.roleName.toUpperCase() === "COMPANY"
        );
        if (!companyRole) return;

        const res = await api.get("/api/Admin/GetUserDetailsBasedOnRole", {
          params: {
            roleId: companyRole.roleId,
            searchText: directorSearch,
            page: 1,
            pageSize: 25,
          },
        });

        setDirectorResults(res.data?.data?.items || []);
      } catch (err) {
        console.error("Director search failed:", err);
      } finally {
        setDirectorLoading(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [directorSearch, showDirectorDropdown]);

  /* --------------------------------------
      FETCH LOCATIONS
  ---------------------------------------*/
  const fetchLocations = async () => {
    try {
      const res = await api.get(ENDPOINTS.getLocations());
      const raw = res.data?.data || [];

      let nationwide = null;
      const list = [];

      raw.forEach(({ locationCode, locationState, locationCity }) => {
        const code = locationCode?.trim();
        const state = locationState?.trim();
        const city = locationCity?.trim();
        if (!code || !state) return;

        const label = city && city !== state ? `${state} (${city})` : state;
        const opt = { value: code, label };

        if (code === "NW") nationwide = opt;
        else list.push(opt);
      });

      list.sort((a, b) => a.label.localeCompare(b.label));
      if (nationwide) list.unshift(nationwide);

      setLocationOptions(list);
    } catch (err) {
      console.error("Location fetch error", err);
    }
  };
const fetchPayTypes = async () => {
  try {
    const res = await api.get(ENDPOINTS.getPayTypes());
    const mapped = (res.data?.data || []).map((item) => ({
      id: Number(item.payId),   // ← force number
      label: item.payType,
    }));
    setPayTypeOptions(mapped);
  } catch (err) {
    console.error("Pay types fetch error", err);
  }
};

  /* --------------------------------------
      ADMIN EDIT MODE DISABLE LOGIC
  ---------------------------------------*/
  useEffect(() => {
    if (!isAdmin || !isEditMode || isLoading) return;

    const { contactName, company, contactEmail } = formData;
    const safe = (v) => (typeof v === "string" ? v.trim() : "");

    setDisabledFields((prev) => ({
      ...prev,
      contactName: safe(contactName) !== "",
      contactEmail: safe(contactEmail) !== "",
      contactPhone: false,
      city: false,
    }));
  }, [isAdmin, isEditMode, isLoading, formData]);

  /* --------------------------------------
      FETCH CATEGORIES
  ---------------------------------------*/
  const fetchCategories = async () => {
    try {
      const res = await api.get(ENDPOINTS.getJobCategories());
      setCategoryOptions(
        (res.data?.data || [])
          .filter((c) => c.activeFlag)
          .map((c) => ({
            id: c.jobCategoryId,
            name: c.jobCategoryName,
          }))
      );
    } catch (err) {
      console.error("Category fetch error", err);
    }
  };

  /* --------------------------------------
      FETCH UNIONS
  ---------------------------------------*/
  const fetchUnions = async () => {
    try {
      const res = await api.get(ENDPOINTS.getUnions());
      if (Array.isArray(res.data)) setUnionOptions(res.data);
    } catch (err) {
      console.error("Union fetch error", err);
    }
  };

  /* --------------------------------------
      FETCH DIRECTORS (Initial)
  ---------------------------------------*/
  const fetchDirectors = async () => {
    try {
      const rolesRes = await api.get(ENDPOINTS.getAllRoles());
      const companyRole = rolesRes.data?.data?.find(
        (r) => r.roleName.toUpperCase() === "COMPANY"
      );
      if (!companyRole) return;

      const usersRes = await api.get(ENDPOINTS.getUsersByRole(), {
        params: { roleId: companyRole.roleId, page: 1, pageSize: 10 },
      });

      setDirectorList(usersRes.data?.data?.items || []);
    } catch (err) {
      console.error("Directors fetch error", err);
    }
  };

  /* --------------------------------------
      NON-ADMIN AUTO-FILL
  ---------------------------------------*/
  useEffect(() => {
    if (isAdmin) return;

    const raw = localStorage.getItem("loggedInUser");
    if (!raw) return;

    let user = null;
    try {
      user = JSON.parse(raw).data;
    } catch {
      return;
    }

    const initial = {};
    const disabled = {};
let contactName = "";

if (user.userFirstName || user.userLastName) {
  contactName = `${user.userFirstName || ""} ${user.userLastName || ""}`.trim();
} else if (user.login) {
  contactName = user.login; // ✅ FIXED (was loginName)
} else if (user.email) {
  contactName = user.email;
}

if (contactName) {
  initial.contactName = contactName;
  disabled.contactName = true;
}


    if (user.companyName) {
      initial.company = user.companyName;
      disabled.company = true;
    }

    if (user.city) {
      initial.city = user.city;
      disabled.city = true;
    }

    if (user.email) {
      initial.contactEmail = user.email;
      disabled.contactEmail = true;
    }

    if (user.phoneNumber) {
      initial.contactPhone = user.phoneNumber;
      disabled.contactPhone = true;
    }

    dispatch({ type: "SET_MULTIPLE_FIELDS", payload: initial });
    setDisabledFields(disabled);
  }, [isAdmin]);

  /* --------------------------------------
      LOAD NOTICE FOR EDIT MODE
  ---------------------------------------*/
useEffect(() => {
  if (
    !urlNoticeId ||
    locationOptions.length === 0 ||
    payTypeOptions.length === 0 ||
    categoryOptions.length === 0  
  ) {
    return;
  }

  const loadNotice = async () => {
    try {
      setIsLoading(true);

      const email = localStorage.getItem("email");

      const res = await api.get(
        ENDPOINTS.getCastingNotices({
          email,
          noticeId: urlNoticeId,
          page: 1,
          pageSize: 50,
        })
      );

      const list = res.data?.data || [];

      const notice = list.find(
        (n) =>
          n.noticeId ===
          (typeof urlNoticeId === "string"
            ? parseInt(urlNoticeId, 10)
            : urlNoticeId)
      );

      if (!notice) return;

      /* ---------- CATEGORY MAPPING ---------- */
      let cats = [];
      if (typeof notice.category === "string") {
        const names = notice.category.split(",").map((v) => v.trim());

        cats = names
          .map((name) => {
            const match = categoryOptions.find(
              (cat) => cat.name.trim().toLowerCase() === name.trim().toLowerCase()
            );
            return match ? match.id : null;
          })
          .filter(Boolean);
      }

      /* ---------- LOCATION MAPPING ---------- */
      const rawLoc = (notice.auditionLocations || "").replace(/["',]/g, "");
      const matches = rawLoc.match(/[A-Z]{2}\d?/g);

      const locCodes = (matches || []).filter((code) =>
        locationOptions.some((opt) => opt.value === code)
      );

      /* ---------- DATE FORMAT ---------- */
      const formatDate = (ds) => {
        if (!ds) return "";
        const d = new Date(ds);
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        return d.toISOString().split("T")[0];
      };

      /* ---------- PAY TYPE MAPPING ---------- */
const payTypeId = notice.paymentType ? Number(notice.paymentType) : "";
      /* ---------- SET FORM DATA ---------- */
      const { paymentType: _rawPaymentType, ...noticeWithoutPaymentType } = notice;
      dispatch({
  type: "SET_MULTIPLE_FIELDS",
  payload: {
    ...noticeWithoutPaymentType,
    unionStatus: notice.unionStatus
      ? notice.unionStatus.split(",").map((v) => v.trim())
      : [],
    jobCategories: cats,
    auditionLocations: locCodes,
    removalDate: formatDate(notice.noticeEndDate),
    PicturesFileName: notice.picturesandDocumentsName || "",
    ScriptsFileName: notice.scripts || "",
    rushCall: notice.rushCall === true || notice.rushCall === "true",
    paymentType: payTypeId,   
  },
});
    } catch (err) {
      console.error("Notice load error", err);
    } finally {
      setIsLoading(false);
    }
  };

  loadNotice();
}, [urlNoticeId, locationOptions, payTypeOptions, categoryOptions ]);

  /* --------------------------------------------------------------------
        ADMIN SELECTS DIRECTOR → FETCH DETAILS
  ---------------------------------------------------------------------*/
  const handleDirectorChange = async (director) => {
    if (!director) return;

    setSelectedDirector(director);
    setSelectedDirectorId(director.userId);
    setDirectorDropdownError("");

    const fullName = `${director.firstName || ""} ${
      director.lastName || ""
    }`.trim();

    dispatch({
      type: "SET_MULTIPLE_FIELDS",
      payload: {
        contactEmail: director.email || "",
        contactName: fullName,
      },
    });

    setDisabledFields((f) => ({
      ...f,
      contactEmail: true,
      contactName: true,
    }));

    // Fetch client details
    try {
      const clientRes = await api.get(
        ENDPOINTS.getAccountPage({ email: director.email })
      );

      const client = clientRes?.data?.data?.[0];

      if (client) {
        dispatch({
          type: "SET_MULTIPLE_FIELDS",
          payload: {
            contactPhone: client.phoneNumber || "",
            company: client.companyName || "",
          },
        });
      }
    } catch (err) {
      console.error("Client details fetch error", err);
    }
  };

  /* ---------------------------------------------
        SUBMIT HANDLER
  ----------------------------------------------*/
  const handleSubmit = async (status) => {
const phoneRegex = /^\d+-\d{4,15}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;
    const alphaRegex = /^[A-Za-z ]+$/;
    const alphanumRegex = /^[A-Za-z0-9 ]+$/;
    const alphanumSpecial =
      /^[A-Za-z0-9 !@#$%^&*()\-_=+{}\[\]:;"'<>,.?/\\|`~\n\r]+$/;

    const safe = (v) => (typeof v === "string" ? v.trim() : "");

    const newErrors = {};

    if (
      !safe(formData.projectTitle) ||
      !alphanumSpecial.test(formData.projectTitle)
    )
      newErrors.projectTitle = "Invalid project title";

    /* if (!safe(formData.contactName) || !alphaRegex.test(formData.contactName))
      newErrors.contactName = "Invalid contact name";

    if (!safe(formData.company)) newErrors.company = "Invalid company"; */

const contactPhone = (formData.contactPhone || "").trim();

if (!phoneRegex.test(contactPhone)) {
  newErrors.contactPhone = "Invalid phone number";
}
    if (!emailRegex.test(safe(formData.contactEmail)))
      newErrors.contactEmail = "Invalid email";

    if (!safe(formData.projectDescription))
      newErrors.projectDescription = "Invalid project description";

    if (!formData.auditionLocations?.length)
      newErrors.auditionLocations = "Select at least one location";

    if (!safe(formData.castingOrShootDatesInfo))
      newErrors.castingOrShootDatesInfo = "Invalid info";

    if (!safe(formData.payScale)) newErrors.payScale = "Invalid pay scale";

    if (!formData.removalDate)
      newErrors.removalDate = "Expiration date required";

if (!formData.unionStatus?.length)
  newErrors.unionStatus = "Select at least one union";
     if (!formData.paymentType)
  newErrors.paymentType = "Pay type required";

    if (isAdmin && !isEditMode && !selectedDirectorId) {
      setDirectorDropdownError("Select a Director");
      return;
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const payload = new FormData();

    Object.entries(formData).forEach(([k, v]) => {
  if (k === "auditionLocations") {
    payload.append("auditionLocations", v.join(""));
  } 
  else if (k === "jobCategories") {
    const selectedCategoryNames = formData.jobCategories
      .map((id) => categoryOptions.find((cat) => cat.id === id)?.name)
      .filter(Boolean);

    payload.append("jobCategory", selectedCategoryNames.join(","));
  } 
  else if (k === "unionStatus") {
    // ✅ FIX: convert array → string
    payload.append("unionStatus", v.join(","));
  } 
  else {
    if (
      !["PicturesFile", "ScriptsFile"].includes(k) &&
      !(
        formData.removePicturesFile &&
        (k === "picturesandDocumentsPath" ||
          k === "picturesandDocumentsName")
      ) &&
      !(
        formData.removeScriptsFile &&
        (k === "scripts" || k === "scriptsPath")
      )
    ) {
      payload.append(k, v);
    }
  }
});

    if (formData.PicturesFile instanceof File)
      payload.append("PicturesFile", formData.PicturesFile);

    if (formData.ScriptsFile instanceof File)
      payload.append("ScriptsFile", formData.ScriptsFile);

    if (formData.removePicturesFile)
      payload.append("removePicturesFile", "true");
    if (formData.removeScriptsFile) payload.append("removeScriptsFile", "true");
   
    payload.append("EmailAddress", formData.contactEmail);

  const hasCreatedUserId =
  formData.createdUserId !== null &&
  formData.createdUserId !== undefined &&
  formData.createdUserId !== "";

const userIdToSend = hasCreatedUserId
  ? formData.createdUserId
  : isAdmin
  ? selectedDirectorId
  : userId;

    payload.append("UserId", userIdToSend);
    payload.set("updatedUserId", userId);

    if (urlNoticeId) payload.append("noticeId", urlNoticeId);

    payload.append("RushCall", formData.rushCall ? "true" : "false");
    payload.append("status", status);
    try {
      setIsLoading(true);
      const res = await api.post(ENDPOINTS.addOrUpdateNotice(), payload);
      const newId = res.data?.data?.noticeId;

      if (newId) {
        setSnackbar({
          open: true,
          message: "Saved successfully!",
          severity: "success",
        });

        if (status === "in-progress") {
          navigate("/role-creator", {
            state: {
              noticeId: newId,
              projectTitle: formData.projectTitle,
              casting: formData,
            },
          });
        } else {
          navigate("/dashboard");
        }
      }
    } catch (err) {
      console.error("Submit error", err);
      setSnackbar({
        open: true,
        message: "Submit failed",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  /* ---------------------------------------------
      RETURN JSX
  ----------------------------------------------*/
  return (
    <>
      {isAdmin ? <AdminNavbar /> : <Navbar />}

      <Box className="background-prop">
        <Box pt={1} maxWidth={1200} mx="auto">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CastingCallForm
                formData={formData}
                dispatch={dispatch}
                errors={errors}
                setErrors={setErrors}
                unionOptions={unionOptions}
                locationOptions={locationOptions}
                fileError={fileError}
                setFileError={setFileError}
                filewordError={filewordError}
                setFilewordError={setFilewordError}
                emailError={emailError}
                setEmailError={setEmailError}
                phoneError={phoneError}
                setPhoneError={setPhoneError}
                categoryOptions={categoryOptions}
                disabledFields={disabledFields}
                handleSubmit={handleSubmit}
                isLoading={isLoading}
                onDirectorChange={handleDirectorChange}
                showDirectorDropdown={showDirectorDropdown}
                directorSearch={directorSearch}
                setDirectorSearch={setDirectorSearch}
                directorResults={directorResults}
                directorLoading={directorLoading}
                selectedDirectorId={selectedDirectorId}
                setSelectedDirectorId={setSelectedDirectorId}
                directorDropdownError={directorDropdownError}
                setDirectorDropdownError={setDirectorDropdownError}
                selectedDirector={selectedDirector}
                setSelectedDirector={setSelectedDirector}
                touched={touched}
                setTouched={setTouched}
                payTypeOptions={payTypeOptions}
                manualRushOverride={manualRushOverride}
                setManualRushOverride={setManualRushOverride}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      <Footer />
    </>
  );
}

export default PostCastingCall;
